import { useMemo, useState } from 'react';
import { Badge, Button, Field, Input, Panel, ScreenHeader, Select, StateBox, Table, humanize, type Column, type CoreBadgeTone } from '@garnet/ui';
import type { OrgStatus } from '@garnet/domain';
import { useAsync } from '../useAsync.js';
import type { AdminApi, AdminOrg, SupportGrant } from '../data/types.js';
import { grantMinutesRemaining, isGrantActive } from '../data/shape.js';
import { NOW } from '../data/fixtures.js';
import { StatStrip, NameCell, dateTimeLabel } from '../components/AdminBits.js';

const ORG_TONE: Record<OrgStatus, CoreBadgeTone> = {
  active: 'b-pass',
  suspended: 'b-warn',
  closed: 'b-fail',
};

const GRANT_TONE: Record<SupportGrant['status'], CoreBadgeTone> = {
  active: 'b-warn',
  expired: 'b-neutral',
  revoked: 'b-fail',
};

function addMinutes(iso: string, minutes: number): string {
  return new Date(Date.parse(iso) + minutes * 60_000).toISOString();
}

/**
 * Customer ops (PRD §20). Org lifecycle, plans and residency, plus the logged, time-boxed
 * support-grant affordance — the ONLY path by which a platform admin reaches customer record
 * content. Every grant is bounded to a time box, attached to a reason, and carries an audit
 * reference; admins may revoke early.
 */
export function Customers({ api }: { api: AdminApi }) {
  const orgs = useAsync(() => api.getOrgs());
  const grantsSeed = useAsync(() => api.getSupportGrants());

  const [extra, setExtra] = useState<SupportGrant[]>([]);
  const [revoked, setRevoked] = useState<ReadonlySet<string>>(new Set());
  const [grantOrg, setGrantOrg] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [minutes, setMinutes] = useState('60');

  const grants = useMemo<SupportGrant[]>(() => {
    const base = [...extra, ...(grantsSeed.data ?? [])];
    return base.map((g) => (revoked.has(g.id) ? { ...g, status: 'revoked' as const } : g));
  }, [extra, grantsSeed.data, revoked]);

  if (orgs.loading || grantsSeed.loading) return <StateBox kind="loading" title="Loading customer ops" />;
  if (orgs.error || !orgs.data || grantsSeed.error || !grantsSeed.data)
    return <StateBox kind="error" title="Could not load customer ops">{orgs.error ?? grantsSeed.error}</StateBox>;

  const orgList = orgs.data;

  const requestGrant = (): void => {
    const org = orgList.find((o) => o.id === grantOrg);
    if (!org || reason.trim() === '') return;
    const mins = Math.max(5, Math.min(240, Number(minutes) || 60));
    const seq = extra.length + 1;
    setExtra((prev) => [
      {
        id: `grant_new_${seq}`,
        org_id: org.id,
        org_name: org.legal_name,
        granted_to: 'you (support)',
        reason: reason.trim(),
        scope: 'record content · read-only',
        granted_at: NOW,
        expires_at: addMinutes(NOW, mins),
        status: 'active',
        audit_ref: `audit_evt_pending_${seq}`,
      },
      ...prev,
    ]);
    setReason('');
    setGrantOrg(null);
  };

  const orgColumns: Column<AdminOrg>[] = [
    { key: 'name', header: 'Organisation', render: (o) => <NameCell name={o.legal_name} sub={`${o.id} · ${o.home_region.toUpperCase()}`} /> },
    { key: 'plan', header: 'Plan', mono: true, render: (o) => humanize(o.plan) },
    { key: 'status', header: 'Status', render: (o) => <Badge tone={ORG_TONE[o.status]}>{o.status}</Badge> },
    {
      key: 'residency',
      header: 'Residency',
      mono: true,
      render: (o) => `pinned · ${o.residency.forbid_third_party_processing ? 'no 3rd-party model' : '3rd-party allowed'}`,
    },
    { key: 'seats', header: 'Seats', mono: true, render: (o) => String(o.seats) },
    { key: 'records', header: 'Records 30d', mono: true, render: (o) => o.records_30d.toLocaleString('en') },
    { key: 'created', header: 'Created', mono: true, render: (o) => o.created_at.slice(0, 10) },
  ];

  const grantColumns: Column<SupportGrant>[] = [
    { key: 'org', header: 'Org', render: (g) => <NameCell name={g.org_name} sub={g.granted_to} /> },
    { key: 'reason', header: 'Reason', render: (g) => <span style={{ fontSize: 12.5 }}>{g.reason}</span> },
    { key: 'scope', header: 'Scope', mono: true, render: (g) => g.scope },
    {
      key: 'window',
      header: 'Time box',
      mono: true,
      render: (g) => {
        const active = isGrantActive(g, NOW);
        return (
          <span style={{ color: active ? 'var(--status-warn-text)' : 'var(--fg-3)' }}>
            {dateTimeLabel(g.granted_at)} → {dateTimeLabel(g.expires_at)}
            {active ? ` · ${grantMinutesRemaining(g, NOW)}m left` : ''}
          </span>
        );
      },
    },
    { key: 'status', header: 'Status', render: (g) => <Badge tone={GRANT_TONE[g.status]} pulse={g.status === 'active'}>{g.status}</Badge> },
    { key: 'audit', header: 'Audit', mono: true, render: (g) => g.audit_ref },
    {
      key: 'action',
      header: '',
      render: (g) =>
        isGrantActive(g, NOW) ? (
          <Button sm variant="ghost" icon="x" onClick={() => setRevoked((p) => new Set(p).add(g.id))}>
            Revoke
          </Button>
        ) : null,
    },
  ];

  const activeGrants = grants.filter((g) => isGrantActive(g, NOW)).length;

  return (
    <div className="screen">
      <ScreenHeader eyebrow="Customer ops" title="Organisations & support grants" lead="Org lifecycle, plans and residency, plus the logged, time-boxed support grant — the only path by which a platform admin reaches customer record content." />
      <StatStrip
        stats={[
          { label: 'Customers', value: orgList.length },
          { label: 'Active', value: orgList.filter((o) => o.status === 'active').length, tone: 'pass' },
          { label: 'Suspended', value: orgList.filter((o) => o.status === 'suspended').length, tone: 'warn' },
          { label: 'Live support grants', value: activeGrants, tone: activeGrants > 0 ? 'warn' : 'default' },
        ]}
      />
      <Panel title="Organisations" right={<span className="metatext">lifecycle · plan · residency</span>} flush bodyStyle={{ maxHeight: 440, overflow: 'auto' }}>
        <Table columns={orgColumns} rows={orgList} rowKey={(o) => o.id} />
      </Panel>
      <Panel
        title="Request support grant"
        right={<span className="metatext">logged · time-boxed access to customer content</span>}
      >
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 220 }}>
            <Field label="Organisation">
              <Select
                value={grantOrg}
                onChange={setGrantOrg}
                options={orgList.map((o) => ({ value: o.id, label: o.legal_name }))}
                placeholder="Select org…"
                ariaLabel="Grant organisation"
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Field label="Reason (logged)">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Case ref / justification" aria-label="Grant reason" />
            </Field>
          </div>
          <div style={{ width: 110 }}>
            <Field label="Minutes" hint="5–240">
              <Input mono value={minutes} onChange={(e) => setMinutes(e.target.value)} inputMode="numeric" aria-label="Grant duration minutes" />
            </Field>
          </div>
          <Button variant="primary" icon="shield" disabled={!grantOrg || reason.trim() === ''} onClick={requestGrant}>
            Request grant
          </Button>
        </div>
      </Panel>
      <Panel title="Support grants" right={<span className="metatext">audit-referenced · revocable</span>} flush bodyStyle={{ maxHeight: 440, overflow: 'auto' }}>
        <Table columns={grantColumns} rows={grants} rowKey={(g) => g.id} />
      </Panel>
    </div>
  );
}
